import { extend, localize } from 'vee-validate'
import {
  required as rule_required,
  email as rule_email,
  min as rule_min,
  max as rule_max,
  min_value as rule_min_value,
  max_value as rule_max_value,
  confirmed as rule_confirmed,
  regex as rule_regex,
  between as rule_between,
  alpha as rule_alpha,
  integer as rule_integer,
  digits as rule_digits,
  alpha_dash as rule_alpha_dash,
  alpha_num as rule_alpha_num,
  length as rule_length,
  numeric as rule_numeric,
  double as rule_double,
} from 'vee-validate/dist/rules'
import ar from 'vee-validate/dist/locale/ar.json'
import en from 'vee-validate/dist/locale/en.json'
import th from 'vee-validate/dist/locale/th.json'
import appEn from '@/libs/i18n/locales/en.json'
import appTh from '@/libs/i18n/locales/th.json'

// eslint-disable-next-line object-curly-newline
import {
  validatorPositive,
  validatorUrlValidator,
  validatorPassword,
  validatorCreditCard,
  validatorDecimal,
} from './validators'

const supportedValidationLocales = ['en', 'th', 'ar']

const customValidationMessages = {
  en: {
    decimal: 'The {_field_} field must be a number',
    positive: 'The {_field_} field must be a positive number',
    creditCard: 'The {_field_} field is not a valid credit card',
    password: 'Your {_field_} must contain at least one uppercase, one lowercase, one special character and one digit',
    url: 'The {_field_} field must be a valid URL',
  },
  th: {
    decimal: '{_field_} ต้องเป็นตัวเลขเท่านั้น',
    positive: '{_field_} ต้องเป็นจำนวนบวก',
    creditCard: '{_field_} ต้องเป็นหมายเลขบัตรเครดิตที่ถูกต้อง',
    password: '{_field_} ต้องมีตัวพิมพ์ใหญ่ ตัวพิมพ์เล็ก อักขระพิเศษ และตัวเลขอย่างน้อยอย่างละ 1 ตัว',
    url: '{_field_} ต้องเป็น URL ที่ถูกต้อง',
  },
}

let activeValidationLocale = 'th'

const normalizeValidationLocale = locale => (supportedValidationLocales.includes(locale) ? locale : 'en')

const flattenLocaleMessages = (messages, prefix = '') => {
  const result = {}

  Object.keys(messages).forEach(key => {
    const value = messages[key]
    const path = prefix ? `${prefix}.${key}` : key

    if (value && typeof value === 'object' && Array.isArray(value) === false) {
      Object.assign(result, flattenLocaleMessages(value, path))
      return
    }

    if (typeof value === 'string') {
      result[path] = value
    }
  })

  return result
}

const createValidationFieldNames = () => {
  const enMessages = flattenLocaleMessages(appEn)
  const thMessages = flattenLocaleMessages(appTh)
  const enNames = {
    email: 'Email',
    password: 'Password',
  }
  const thNames = {
    email: 'อีเมล',
    password: 'รหัสผ่าน',
  }

  Object.keys({ ...enMessages, ...thMessages }).forEach(key => {
    const enLabel = enMessages[key]
    const thLabel = thMessages[key]

    if (enLabel) enNames[key] = enLabel
    if (thLabel) thNames[key] = thLabel

    if (enLabel && thLabel) {
      enNames[thLabel] = enLabel
      thNames[enLabel] = thLabel
    }
  })

  return { en: enNames, th: thNames }
}

const validationFieldNames = createValidationFieldNames()

const getStoredValidationLocale = () => {
  if (typeof localStorage === 'undefined') return 'th'

  return localStorage.getItem('locale') || 'th'
}

const resolveValidationFieldName = field => validationFieldNames[activeValidationLocale]?.[field] || field

const getCustomValidationMessage = key => field => {
  const messages = customValidationMessages[activeValidationLocale] || customValidationMessages.en
  const message = messages[key] || customValidationMessages.en[key]

  return message.replace('{_field_}', resolveValidationFieldName(field))
}

// ////////////////////////////////////////////////////////
// General
// ////////////////////////////////////////////////////////

export const required = extend('required', rule_required)

export const email = extend('email', rule_email)

export const min = extend('min', rule_min)

export const max = extend('max', rule_max)

// eslint-disable-next-line camelcase
export const min_value = extend('min_value', rule_min_value)

// eslint-disable-next-line camelcase
export const max_value = extend('max_value', rule_max_value)

export const confirmed = extend('confirmed', rule_confirmed)

export const regex = extend('regex', rule_regex)

export const between = extend('between', rule_between)

export const alpha = extend('alpha', rule_alpha)

export const integer = extend('integer', rule_integer)

export const digits = extend('digits', rule_digits)

export const alphaDash = extend('alpha-dash', rule_alpha_dash)

export const alphaNum = extend('alpha-num', rule_alpha_num)

export const length = extend('length', rule_length)

export const numeric = extend('numeric', rule_numeric)

export const double = extend('double', rule_double)

export const decimal = extend('decimal', {
  validate: validatorDecimal,
  message: getCustomValidationMessage('decimal'),
})

export const positive = extend('positive', {
  validate: validatorPositive,
  message: getCustomValidationMessage('positive'),
})

export const credit = extend('credit-card', {
  validate: validatorCreditCard,
  message: getCustomValidationMessage('creditCard'),
})

export const password = extend('password', {
  validate: validatorPassword,
  message: getCustomValidationMessage('password'),
})

export const url = extend('url', {
  validate: validatorUrlValidator,
  message: getCustomValidationMessage('url'),
})

// Install English, Thai and Arabic localizations.
localize({
  en: {
    messages: en.messages,
    names: validationFieldNames.en,
    fields: {
      password: {
        min: '{_field_} is too short, you want to get hacked?',
      },
    },
  },
  th: {
    messages: th.messages,
    names: validationFieldNames.th,
    fields: {
      password: {
        min: '{_field_} ต้องมีความยาวอย่างน้อย {length} ตัวอักษร',
      },
    },
  },
  ar: {
    messages: ar.messages,
    names: {
      email: 'البريد الإلكتروني',
      password: 'كلمة السر',
    },
    fields: {
      password: {
        min: 'كلمة السر قصيرة جداً سيتم اختراقك',
      },
    },
  },
})

export const setValidationLocale = locale => {
  activeValidationLocale = normalizeValidationLocale(locale)
  localize(activeValidationLocale)
}

setValidationLocale(getStoredValidationLocale())
// ////////////////////////////////////////////////////////
// NOTE:
// Quasar validation for reference only
// Remove this note once development is finished and make sure to
// to convert all of them in veevalidate version
// ////////////////////////////////////////////////////////

// export const required = (val) => {
//   return (val && val.length > 0) || '*Field is required'
// }

// export const required_obj = (obj) => {
//   if (obj === null || obj === undefined) return '*Field is required'
//   return (Object.entries(obj).length > 0 && obj.constructor === Object) || '*Field is required'
// }

// export const no_blank_spaces_arr = (arr) => {
//   return arr.every(val => (val.trim() && val.trim().length > 0)) || 'Blank Spaces are not allowed'
// }

// export const url = val => {
//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   // Used
//   // https://stackoverflow.com/questions/4314741/url-regex-validation

//   // Other
//   // https://stackoverflow.com/questions/5717093/check-if-a-javascript-string-is-a-url
//   // https://www.w3resource.com/javascript-exercises/javascript-regexp-exercise-9.php
//   // https://www.geeksforgeeks.org/how-to-validate-url-using-regular-expression-in-javascript/

//   /* eslint-disable no-useless-escape */
//   const re = /^(http[s]?:\/\/){0,1}(www\.){0,1}[a-zA-Z0-9\.\-]+\.[a-zA-Z]{2,5}[\.]{0,1}/
//   /* eslint-enable no-useless-escape */
//   return re.test(val) || 'URL is invalid'
// }

// export const date = val => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   // https://github.com/quasarframework/quasar/blob/dev/ui/src/utils/patterns.js
//   return /^-?[\d]+\/[0-1]\d\/[0-3]\d$/.test(val) || 'Date is invalid'
// }

// export const max = (val, max) => {

//   // If blank return
//   if (val === undefined || val === null) return true

//   return val.length <= max || `More than ${max} characters are not allowed`
// }

// export const max_arr = (val, max) => {
//   return val.length <= max || `More than ${max} values are not allowed`
// }

// export const min = (val, min) => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   return val.length >= min || `Minimum ${min} characters are required`
// }

// export const num_range = (val, min, max) => {

//   // If blank return
//   if (val === undefined || val === null || val.length === 0) return true

//   const msg = 'Value is invalid'
//   if (min === null) return val <= max || msg
//   else if (max === null) return val >= min || msg
//   else return (val >= min && val <= max) || msg
// }
